import { useQuery } from '@apollo/client/react';
import { Link, useParams } from 'react-router';
import { ArrowLeft } from 'lucide-react';
import { graphql } from '@/gql';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { OverviewPanel } from '@/components/ui/OverviewPanel';
import { formatCurrency, formatDate } from '@/lib/format';
import { useBackHref } from '@/hooks/useListLocation';

const TaxReportDayDoc = graphql(/* GraphQL */ `
  query TaxReportDay($recognitionDate: String!) {
    taxReport(recognitionDate: $recognitionDate) {
      periodStart
      periodEnd
      salesDays
      salesIncludingTax
      totalLoss
      cashTrade
      tips
      taxBreakdown {
        taxPercentage
        salesExcludingTax
        taxAmount
        salesIncludingTax
      }
      paymentMethodBreakdown {
        paymentMethod
        total
      }
    }
  }
`);

const TaxReportMonthDoc = graphql(/* GraphQL */ `
  query TaxReportMonth($year: Int!, $month: Int!) {
    taxReport(year: $year, month: $month) {
      periodStart
      periodEnd
      salesDays
      salesIncludingTax
      totalLoss
      cashTrade
      tips
      taxBreakdown {
        taxPercentage
        salesExcludingTax
        taxAmount
        salesIncludingTax
      }
      paymentMethodBreakdown {
        paymentMethod
        total
      }
    }
  }
`);

type TaxReportRouteParams = {
  date?: string;
  year?: string;
  month?: string;
};

/**
 * Omzetstaat detail view for a single day or full month (replaces legacy PDF).
 */
export function TaxReportDetailPage() {
  const backHref = useBackHref('/tax-reports');
  const { date, year, month } = useParams<TaxReportRouteParams>();
  const isMonth = year !== undefined && month !== undefined;

  const dayQuery = useQuery(TaxReportDayDoc, {
    variables: { recognitionDate: date ?? '' },
    skip: isMonth || !date,
  });
  const monthQuery = useQuery(TaxReportMonthDoc, {
    variables: { year: parseInt(year ?? '0', 10), month: parseInt(month ?? '0', 10) },
    skip: !isMonth,
  });

  const { data, loading, error } = isMonth ? monthQuery : dayQuery;
  const report = data?.taxReport;

  const periodLabel = isMonth
    ? report?.periodEnd
      ? `${formatDate(report.periodStart)} – ${formatDate(report.periodEnd)}`
      : report?.periodStart
        ? formatDate(report.periodStart)
        : 'Month'
    : report?.periodStart
      ? formatDate(report.periodStart)
      : 'Day';

  const paymentTotal =
    report?.paymentMethodBreakdown.reduce((sum, line) => sum + line.total, 0) ?? 0;

  if (loading) {
    return <div className="p-10 text-center text-sm text-gray-500">Loading…</div>;
  }
  if (error) {
    return <EmptyState title="Could not load report" description={error.message} />;
  }
  if (!report) {
    return <EmptyState title="Report not found" description="This period has no data." />;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Link to={backHref} id="admin.taxReports.back">
          <Button variant="outline" size="sm" leftIcon={<ArrowLeft className="h-4 w-4" />}>
            Back to tax reports
          </Button>
        </Link>
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Omzetstaat</h1>
          <p className="mt-1 flex flex-wrap items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <span>{periodLabel}</span>
            <Badge tone={isMonth ? 'brand' : 'neutral'}>{isMonth ? 'Month total' : 'Day'}</Badge>
          </p>
        </div>
      </div>

      <OverviewPanel
        stats={[
          {
            id: 'sales',
            label: 'Sales incl. tax',
            value: <span id="admin.taxReport.sales">{formatCurrency(report.salesIncludingTax)}</span>,
          },
          {
            id: 'loss',
            label: 'Total loss',
            value: formatCurrency(report.totalLoss),
          },
          {
            id: 'cash',
            label: 'Cash trade',
            value: <span id="admin.taxReport.cashTrade">{formatCurrency(report.cashTrade)}</span>,
          },
          {
            id: 'tips',
            label: 'Total tips',
            value: <span id="admin.taxReport.tips">{formatCurrency(report.tips)}</span>,
          },
          {
            id: 'days',
            label: 'Sales days',
            value: String(report.salesDays),
          },
        ]}
      />

      <Card title="Tax breakdown">
        {report.taxBreakdown.length === 0 ? (
          <p className="text-sm text-gray-500">No taxable sales in this period.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[28rem] text-left text-sm">
              <thead className="text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-2 py-2">Rate</th>
                  <th className="px-2 py-2 text-right">Excl. tax</th>
                  <th className="px-2 py-2 text-right">Tax</th>
                  <th className="px-2 py-2 text-right">Incl. tax</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-white/10">
                {report.taxBreakdown.map((line) => {
                  const rateId = `admin.taxReport.rate.${Math.round(line.taxPercentage)}`;
                  return (
                    <tr key={line.taxPercentage} id={rateId}>
                      <td className="px-2 py-2">{line.taxPercentage}%</td>
                      <td id={`${rateId}.excl`} className="px-2 py-2 text-right tabular-nums">
                        {formatCurrency(line.salesExcludingTax)}
                      </td>
                      <td id={`${rateId}.tax`} className="px-2 py-2 text-right tabular-nums">
                        {line.taxPercentage > 0 ? formatCurrency(line.taxAmount) : '—'}
                      </td>
                      <td
                        id={`${rateId}.incl`}
                        className="px-2 py-2 text-right font-medium tabular-nums"
                      >
                        {formatCurrency(line.salesIncludingTax)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card title="Payment methods">
        {report.paymentMethodBreakdown.length === 0 ? (
          <p className="text-sm text-gray-500">No payments in this period.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[20rem] text-left text-sm">
              <thead className="text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-2 py-2">Method</th>
                  <th className="px-2 py-2 text-right">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-white/10">
                {report.paymentMethodBreakdown.map((line) => {
                  const paymentId = `admin.taxReport.payment.${line.paymentMethod.toLowerCase()}`;
                  return (
                    <tr key={line.paymentMethod} id={paymentId}>
                      <td className="px-2 py-2 uppercase">{line.paymentMethod}</td>
                      <td id={`${paymentId}.amount`} className="px-2 py-2 text-right tabular-nums">
                        {formatCurrency(line.total)}
                      </td>
                    </tr>
                  );
                })}
                <tr className="font-semibold">
                  <td className="px-2 py-2">Total</td>
                  <td id="admin.taxReport.payment.total" className="px-2 py-2 text-right tabular-nums">
                    {formatCurrency(paymentTotal)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
